import React, { useEffect, useState } from "react";
import { RefreshCw, Film, PlayCircle, Eye, DollarSign, Loader2, Video, Wand2, Users, Languages, Volume2, User, AlertCircle, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

const auth = () => {
  const t = localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function Stat({ icon: Icon, label, value, hint }) {
  return (
    <div className="fl-card p-4 bg-white/[0.02] border-white/10">
      <div className="flex items-center gap-2 text-white/60 text-xs fl-mono uppercase tracking-widest"><Icon className="w-3.5 h-3.5 text-emerald-400" /> {label}</div>
      <div className="text-3xl font-black text-white mt-1">{value ?? "—"}</div>
      {hint && <div className="text-xs text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

function RankList({ icon: Icon, title, rows, keyName, capitalize }) {
  return (
    <div className="fl-card p-4 bg-white/[0.02] border-white/10">
      <div className="flex items-center gap-2 text-white/60 text-xs fl-mono uppercase tracking-widest mb-3"><Icon className="w-3.5 h-3.5 text-emerald-400" /> {title}</div>
      {rows.length === 0 ? <div className="text-slate-500 text-sm">No data yet.</div> : (
        <div className="grid grid-cols-2 gap-1.5">
          {rows.map((r, i) => (
            <div key={r[keyName] || i} className="flex items-center justify-between text-sm border-b border-white/5 py-1">
              <span className={`text-white/90 ${capitalize ? "capitalize" : ""}`}>{String(r[keyName] || "—").replace("-", " ")}</span>
              <span className="text-emerald-400 font-semibold">{r.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function usdFromCents(c) { return `$${((c || 0) / 100).toFixed(2)}`; }
function niceBytes(b) {
  if (!b) return "0 B";
  const u = ["B","KB","MB","GB","TB"]; let i = 0; let v = b;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${u[i]}`;
}

// ─── Showo (film platform) ──────────────────────────────────────────
export function ShowoInsightsCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setBusy(true); setErr("");
    try {
      const { data: d } = await api.get("/admin/showo/stats", { headers: auth() });
      setData(d);
    } catch (e) { setErr(e?.response?.data?.detail || "Failed to load"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  if (busy && !data) return <div className="fl-card p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-emerald-400" /></div>;
  if (err) return <div className="fl-card p-6 border-red-400/30 text-red-300 text-sm">{err}</div>;
  if (!data) return null;

  const { films, engagement, top_categories, top_genres, top_films, top_creators, recent_films } = data;

  return (
    <div className="space-y-5" data-testid="showo-insights-root">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="fl-display text-2xl font-bold">Showo Insights</h2>
        <button onClick={load} disabled={busy} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="showo-insights-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Film}       label="Films"        value={films.total}          hint={`${films.published} published · +${films.last_24h} last 24h`} />
        <Stat icon={PlayCircle} label="Native / YT"  value={`${films.native}/${films.youtube}`} hint={`${films.from_make} auto-made`} />
        <Stat icon={Eye}        label="Total plays"  value={engagement.total_views.toLocaleString()} hint={`+${films.last_7d} films last 7d · +${films.last_30d} last 30d`} />
        <Stat icon={DollarSign} label="Tips earned"  value={usdFromCents(engagement.total_tips_cents)} hint="from tip jars" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <RankList icon={Video} title="Top categories" rows={top_categories} keyName="category" capitalize />
        <RankList icon={Wand2} title="Top genres"     rows={top_genres}     keyName="genre"    capitalize />
      </div>

      <div className="fl-card p-4 bg-white/[0.02] border-white/10">
        <div className="flex items-center gap-2 text-white/60 text-xs fl-mono uppercase tracking-widest mb-3"><Eye className="w-3.5 h-3.5 text-emerald-400" /> Most-watched films ({top_films.length})</div>
        {top_films.length === 0 ? <div className="text-slate-500 text-sm">No films yet.</div> : (
          <div className="grid sm:grid-cols-2 md:grid-cols-5 gap-2">
            {top_films.map(f => (
              <div key={f.film_id} className="fl-card p-2 bg-white/[0.02] border-white/5" data-testid={`showo-top-${f.film_id}`}>
                <div className="aspect-[9/16] rounded overflow-hidden bg-black/50">
                  {f.cover_url ? <img src={f.cover_url} alt="" className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center text-white/30"><Film className="w-6 h-6" /></div>}
                </div>
                <div className="text-xs text-white truncate mt-1 font-semibold">{f.title}</div>
                <div className="text-[10px] text-emerald-400 fl-mono uppercase tracking-widest">{(f.views || 0).toLocaleString()} plays · {usdFromCents(f.tips_cents)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="fl-card p-4 bg-white/[0.02] border-white/10">
        <div className="flex items-center gap-2 text-white/60 text-xs fl-mono uppercase tracking-widest mb-3"><Users className="w-3.5 h-3.5 text-emerald-400" /> Top creators ({top_creators.length})</div>
        {top_creators.length === 0 ? <div className="text-slate-500 text-sm">No creators yet.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-white/50 text-xs uppercase tracking-widest"><tr><th className="text-left py-2">Email</th><th className="text-right">Films</th><th className="text-right">Plays</th><th className="text-right">Tips</th></tr></thead>
              <tbody>
                {top_creators.map(c => (
                  <tr key={c.email} className="border-t border-white/5">
                    <td className="py-2 text-white/90 truncate max-w-[220px]">{c.email}</td>
                    <td className="text-right text-emerald-400 font-semibold">{c.films}</td>
                    <td className="text-right text-white/80">{(c.views || 0).toLocaleString()}</td>
                    <td className="text-right text-white/80">{usdFromCents(c.tips_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="fl-card p-4 bg-white/[0.02] border-white/10">
        <div className="flex items-center gap-2 text-white/60 text-xs fl-mono uppercase tracking-widest mb-3"><Film className="w-3.5 h-3.5 text-emerald-400" /> Recent uploads ({recent_films.length})</div>
        {recent_films.length === 0 ? <div className="text-slate-500 text-sm">No films yet.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-white/50 text-xs uppercase tracking-widest"><tr><th className="text-left py-2">When</th><th className="text-left">Title</th><th className="text-left">Owner</th><th className="text-left">Category</th><th className="text-right">Plays</th></tr></thead>
              <tbody>
                {recent_films.map(f => (
                  <tr key={f.film_id} className="border-t border-white/5">
                    <td className="py-2 text-white/50 font-mono text-[11px]">{(f.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="text-white/90 truncate max-w-[240px]">{f.title}</td>
                    <td className="text-white/70 truncate max-w-[180px]">{f.owner_email || "—"}</td>
                    <td className="text-white/60 capitalize">{(f.category || "—").replace("_"," ")}</td>
                    <td className="text-right text-emerald-400 font-semibold">{(f.views || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="text-white/30 text-[10px] fl-mono uppercase tracking-widest">Generated {(data.generated_at || "").slice(0, 19).replace("T", " ")} UTC</div>
    </div>
  );
}

// ─── Showo Make (movie generator) ───────────────────────────────────
export function ShowoMakeInsightsCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setBusy(true); setErr("");
    try {
      const { data: d } = await api.get("/admin/showo/make/stats", { headers: auth() });
      setData(d);
    } catch (e) { setErr(e?.response?.data?.detail || "Failed to load"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  if (busy && !data) return <div className="fl-card p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-emerald-400" /></div>;
  if (err) return <div className="fl-card p-6 border-red-400/30 text-red-300 text-sm">{err}</div>;
  if (!data) return null;

  const { jobs, faces, output, top_genres, top_lengths, top_languages, top_voices, top_creators, failed_jobs, recent_jobs } = data;

  return (
    <div className="space-y-5" data-testid="showo-make-insights-root">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="fl-display text-2xl font-bold">Showo Make Insights</h2>
        <button onClick={load} disabled={busy} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="showo-make-insights-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Video}         label="Movies rendered"  value={jobs.total}       hint={`${jobs.ready} ready · ${jobs.failed} failed · ${jobs.running} in flight`} />
        <Stat icon={CheckCircle2}  label="Success rate"     value={`${jobs.success_rate}%`} hint={`+${jobs.last_24h} last 24h · +${jobs.last_7d} last 7d`} />
        <Stat icon={User}          label="Movies with face" value={jobs.with_face}   hint={`${faces.total} face uploads (${faces.last_24h} last 24h)`} />
        <Stat icon={DollarSign}    label="Credits burned"   value={`$${output.approx_credits_spent_usd}`} hint={`${output.total_scenes_rendered} scenes · ${niceBytes(output.total_bytes)} rendered`} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <RankList icon={Wand2}     title="Top genres"    rows={top_genres}    keyName="genre"    capitalize />
        <RankList icon={Video}     title="Top lengths"   rows={top_lengths}   keyName="length"   capitalize />
        <RankList icon={Languages} title="Top languages" rows={top_languages} keyName="language" />
        <RankList icon={Volume2}   title="Top voices"    rows={top_voices}    keyName="voice"    capitalize />
      </div>

      <div className="fl-card p-4 bg-white/[0.02] border-white/10">
        <div className="flex items-center gap-2 text-white/60 text-xs fl-mono uppercase tracking-widest mb-3"><Users className="w-3.5 h-3.5 text-emerald-400" /> Top creators ({top_creators.length})</div>
        {top_creators.length === 0 ? <div className="text-slate-500 text-sm">No renders yet.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-white/50 text-xs uppercase tracking-widest"><tr><th className="text-left py-2">Email</th><th className="text-right">Renders</th><th className="text-right">Successful</th></tr></thead>
              <tbody>
                {top_creators.map(c => (
                  <tr key={c.email} className="border-t border-white/5">
                    <td className="py-2 text-white/90 truncate max-w-[280px]">{c.email}</td>
                    <td className="text-right text-emerald-400 font-semibold">{c.renders}</td>
                    <td className="text-right text-white/80">{c.ready}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {failed_jobs.length > 0 && (
        <div className="fl-card p-4 bg-white/[0.02] border border-red-400/20">
          <div className="flex items-center gap-2 text-red-300 text-xs fl-mono uppercase tracking-widest mb-3"><AlertCircle className="w-3.5 h-3.5" /> Recent failures ({failed_jobs.length})</div>
          <div className="space-y-1.5">
            {failed_jobs.map(f => (
              <div key={f.job_id} className="text-sm p-2 rounded bg-white/[0.02] border border-white/5" data-testid={`showo-make-fail-${f.job_id}`}>
                <div className="flex justify-between gap-2 flex-wrap text-xs">
                  <span className="text-red-300 fl-mono uppercase">{f.genre || "—"} · {f.length || "—"} · {f.owner_email}</span>
                  <span className="text-white/40 font-mono">{(f.created_at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
                <div className="text-white/80 mt-1 line-clamp-2">{f.prompt}</div>
                <div className="text-red-400/80 mt-1 text-[11px]">{f.error || "—"}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="fl-card p-4 bg-white/[0.02] border-white/10">
        <div className="flex items-center gap-2 text-white/60 text-xs fl-mono uppercase tracking-widest mb-3"><Video className="w-3.5 h-3.5 text-emerald-400" /> Recent renders ({recent_jobs.length})</div>
        {recent_jobs.length === 0 ? <div className="text-slate-500 text-sm">No renders yet.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-white/50 text-xs uppercase tracking-widest"><tr><th className="text-left py-2">When</th><th className="text-left">Title / prompt</th><th className="text-left">Owner</th><th className="text-left">Genre</th><th className="text-left">Lang</th><th className="text-right">Status</th></tr></thead>
              <tbody>
                {recent_jobs.map(j => (
                  <tr key={j.job_id} className="border-t border-white/5">
                    <td className="py-2 text-white/50 font-mono text-[11px]">{(j.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="text-white/90 truncate max-w-[260px]">{j.title || j.prompt}{j.face_id ? " · with face" : ""}</td>
                    <td className="text-white/70 truncate max-w-[180px]">{j.owner_email || "—"}</td>
                    <td className="text-white/60 capitalize">{j.genre || "—"}</td>
                    <td className="text-white/60 font-mono uppercase">{j.language || "en"}</td>
                    <td className={`text-right fl-mono uppercase text-[11px] ${j.status === "ready" ? "text-emerald-300" : j.status === "failed" ? "text-red-400" : "text-yellow-300"}`}>{j.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="text-white/30 text-[10px] fl-mono uppercase tracking-widest">Generated {(data.generated_at || "").slice(0, 19).replace("T", " ")} UTC</div>
    </div>
  );
}
